import { OcppEvent, SessionEntity, SessionStatus } from "@/domain/entities";
import { formatCurrency } from "@/utils";
import moment from "moment";
import { ActivityLog } from "./ActivityLog";

export class Session {
  id: string;
  charger: string;
  connector: string = "";
  user_name: string = "";
  started_at: string = "";
  finished_at: string = "";
  duration: string = "00h00m";
  idle_mins: number = 0;
  idle_value: string = "R$ 0,00";
  energy: string = "0kW";
  income: string = "R$ 0,00";
  location_name: string;
  plan: string = "";
  payment_method: string;
  status: StatusConfig;
  activity_log: ActivityLog[] = [];
  pre_auth_value: string = "";
  pre_auth_status: string = "";

  constructor(session: SessionEntity) {
    this.id = String(session.id);
    // TODO esse charger id é o id memo, tinha que vir o ocpp_id
    this.charger = session.charger_id;
    if (session.user && session.user.name) {
      this.user_name = session.user.name;
    }
    this.status = statusConfig[session.status];
    this.payment_method = paymentStatuses[session.payment_method];

    if (session.connector) {
      this.connector = session.connector.id;
      if (session.connector.pricing_profile) {
        this.plan = session.connector.pricing_profile.title;
      }
    }
    if (session.started_at) {
      this.started_at = moment(session.started_at).format("DD/MM/YYYY");
    }
    if (session.finished_at) {
      this.finished_at = session.finished_at;
    }
    if (session.started_at && session.finished_at) {
      this.duration = Session.formatDuration({
        finished_at: session.finished_at,
        started_at: session.started_at,
      });
    }
    if (session.charged_amount) {
      this.energy = `${parseFloat(session.charged_amount).toFixed(2)}kW`;
    }
    if (session.value) {
      this.income = formatCurrency(Number(session.value));
    }
    if (session.idle_value) {
      this.idle_value = formatCurrency(Number(session.idle_value));
    }
    if (session.idle_mins) {
      this.idle_mins = Number(moment.duration(session.idle_mins, 'minutes').asMinutes())
    }

    if (session.ocpp_events) {
      this.activity_log = session.ocpp_events.map(
        (elem: OcppEvent) => new ActivityLog(elem)
      );
    }
    this.location_name = session.connector.charger?.location?.title ?? ''

    if (session.pre_auth) {
      this.pre_auth_value = formatCurrency(Number(session.pre_auth.value));
      if (session.pre_auth.charge_status === 'canceled') {
        this.pre_auth_status = 'Reservado e cancelado';
      } else {
        this.pre_auth_status = session.pre_auth.charge_status;
      }
    }
  }

  static empty: Session = {
    id: "",
    charger: "",
    connector: "",
    user_name: "",
    started_at: "",
    idle_mins: 0,
    idle_value: "",
    location_name: "",
    finished_at: "",
    duration: "",
    energy: "",
    income: "",
    plan: "",
    payment_method: "",
    status: {
      value: "",
      label: "",
      color: "",
    },
    activity_log: [],
    pre_auth_value: "",
    pre_auth_status: "",
  };

  static formatDuration = ({
    finished_at,
    started_at,
  }: {
    finished_at: string;
    started_at: string;
  }): string => {
    const diff = moment(finished_at).diff(moment(started_at));
    const duration = moment.duration(diff);
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${String(hours).padStart(2, "0")}h${String(minutes).padStart(
      2,
      "0"
    )}min`;
  };
}

type StatusConfig = {
  value: string;
  label: string;
  color: string;
};

const paymentStatuses: Record<string, string> = {
  credit_card: "Cartão de crédito",
  balance: "Saldo",
};

const statusConfig: Record<SessionStatus, StatusConfig> = {
  pending: {
    value: "pending",
    label: "Pendente",
    color: "warning",
  },
  concluded: {
    value: "concluded",
    label: "Finalizado",
    color: "success",
  },
  charging: {
    value: "charging",
    label: "Em andamento",
    color: "info",
  },
  canceled: {
    value: "canceled",
    label: "Cancelado",
    color: "danger",
  },
};
